import { api } from "./api";

const VISITOR_KEY = "st_visitor";
const ATTR_FIRST_KEY = "st_attr_first";
const ATTR_LAST_KEY = "st_attr_last";

// Persistent anonymous visitor id (acts as the "cookie" for unique-visitor dedupe).
export function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = (crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return `v_${Date.now()}`;
  }
}

// Map a referrer hostname to a friendly channel name.
function channelFromHost(host) {
  const h = host.replace(/^www\./, "").toLowerCase();
  const map = [
    ["google.", "google"], ["bing.", "bing"], ["duckduckgo.", "duckduckgo"], ["yahoo.", "yahoo"],
    ["facebook.", "facebook"], ["l.facebook", "facebook"], ["fb.", "facebook"],
    ["instagram.", "instagram"], ["l.instagram", "instagram"],
    ["t.co", "twitter"], ["twitter.", "twitter"], ["x.com", "twitter"],
    ["tiktok.", "tiktok"], ["youtube.", "youtube"], ["youtu.be", "youtube"],
    ["reddit.", "reddit"], ["linkedin.", "linkedin"], ["pinterest.", "pinterest"],
    ["snapchat.", "snapchat"], ["whatsapp", "whatsapp"],
  ];
  for (const [needle, name] of map) if (h.includes(needle)) return name;
  return h; // unknown referrer -> its hostname
}

function readJSON(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}

// A real attribution signal from THIS page load (UTM params or external referrer), or null if "direct".
function currentSignal() {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = p.get("utm_source");
    if (utm) {
      return { source: utm.toLowerCase(), medium: (p.get("utm_medium") || "").toLowerCase(), campaign: p.get("utm_campaign") || "" };
    }
    if (document.referrer) {
      const refHost = new URL(document.referrer).hostname;
      if (refHost && refHost !== window.location.hostname) {
        return { source: channelFromHost(refHost), medium: "referral", campaign: "" };
      }
    }
  } catch { /* ignore */ }
  return null;
}

// Resolve first-touch (kept until it expires past the attraction's window) and last-touch
// (updated whenever a new real signal appears; sticky otherwise).
// windowDays: 0/undefined = never expire; 30/60/90 = re-attribute after that many days.
function getAttribution(windowDays) {
  let first = readJSON(ATTR_FIRST_KEY);
  let last = readJSON(ATTR_LAST_KEY);
  const signal = currentSignal();
  const now = Date.now();

  // Backfill a timestamp on legacy stored attribution so expiry can apply going forward.
  if (first && !first.ts) { first.ts = now; try { localStorage.setItem(ATTR_FIRST_KEY, JSON.stringify(first)); } catch { /* ignore */ } }

  // Expire first-touch once it's older than the attraction's window.
  if (first && Number.isFinite(windowDays) && windowDays > 0 && first.ts && (now - first.ts) > windowDays * 86400000) {
    first = null;
  }

  if (!first) {
    first = { ...(signal || { source: "direct", medium: "", campaign: "" }), ts: now };
    try { localStorage.setItem(ATTR_FIRST_KEY, JSON.stringify(first)); } catch { /* ignore */ }
  }
  if (signal) {
    last = { ...signal, ts: now };
    try { localStorage.setItem(ATTR_LAST_KEY, JSON.stringify(last)); } catch { /* ignore */ }
  }
  if (!last) last = first;
  return { first, last };
}

// Attribution payload for server-side events (e.g. the "paid" step recorded on the backend
// when a payment truly succeeds). Resolves first/last touch using the attraction's window.
export function getAttributionPayload(windowDays) {
  const { first, last } = getAttribution(Number.isFinite(windowDays) ? windowDays : 90);
  return {
    visitor_id: getVisitorId(),
    source: first.source, medium: first.medium, campaign: first.campaign,
    last_source: last.source, last_medium: last.medium, last_campaign: last.campaign,
  };
}

// Fire-and-forget booking-funnel step.
// step: visit | date | slot | tickets | checkout | paid
export function trackFunnel(step, { attraction_id, event_id, revenue_pence, window_days, tickets, value_pence, date_id, slot_id, order_id } = {}) {
  if (!attraction_id) return;
  const win = Number.isFinite(window_days) ? window_days : 90; // default matches attraction default
  const { first, last } = getAttribution(win);
  try {
    api.post("/public/track", {
      visitor_id: getVisitorId(), step, attraction_id, event_id, revenue_pence,
      tickets, value_pence, date_id, slot_id, order_id,
      source: first.source, medium: first.medium, campaign: first.campaign,
      last_source: last.source, last_medium: last.medium, last_campaign: last.campaign,
    });
  } catch {
    /* never block the UI on analytics */
  }
}
